import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { DataSourceService } from '../../service/datatsource.service';
import { AGENT_MODEL } from '../../../../../common-lib/src/lib/providers/device/device';
import {
  DevicesService,
  DialogService,
  FusionTransformationService,
  NotificationService,
  TabulatorService
} from '@nrwl/common-lib';
import { ViaviTableComponent } from '@nrwl/viavi-tabulator';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BulkRenamingDialogComponent } from './bulk-renaming-dialog/bulk-renaming-dialog.component';
import { DataDisplayDialogComponent } from './data-display-dialog/data-display-dialog.component';
import { ViewResultsDialogComponent } from './view-results-dialog/view-results-dialog.component';
import { MatSelectChange } from '@angular/material/select';

@Component({
  selector: 'nrwl-uploads-settings',
  templateUrl: './uploads-settings.component.html',
  styleUrls: ['./uploads-settings.component.scss']
})
export class UploadsSettingsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(ViaviTableComponent) flowsTable: ViaviTableComponent;
  @ViewChild('file') file: ElementRef<HTMLInputElement>;
  public files: Set<File> = new Set();
  isLoading = false;
  tabName: string;
  uploadMsg: string;
  uploadErrorMsg: string;
  uploadProcessing = false;
  fileAccept = '.pkg';
  showProgressBar = false;
  progressCompleted = 0;
  uploadErrorIcon: string;
  title = 'Uploads';
  uploadAgentType = [
    { name: AGENT_MODEL.QT600, value: AGENT_MODEL.QT600 },
    { name: AGENT_MODEL.MAP2100, value: AGENT_MODEL.MAP2100 },
    { name: 'JMEP', value: 'JMEP' },
    { name: AGENT_MODEL.VSPA, value: AGENT_MODEL.VSPA }
  ];
  dataAvailable = false;
  selectedAgentType = this.uploadAgentType[0].value;
  selectedFile: File | null = null;
  selectedFileName = '';
  jmepImageList: any[] = [];
  agentImageList: any[] = [];
  table: any;
  mappingTable: any;
  isCheckBoxEnabled = false;
  showClearButton = false;
  jmepKeysFileDateString: string;
  jmepKeysFileName: any;
  showBulkRenamingFiles: boolean;
  getAllMetaDataFileResponse: any;
  jmepFieldMappingFilesList: any;
  getJmepFileListReq: any;
  applyRenamingDialogRef: MatDialogRef<any>;
  isValidAgentFileExtension = false;
  jmepBulkConfigInputFileDownloadPath =
    '/vibe/inventory/bulkconfig/files?type=config&groupName=rename';
  private readonly unsubscribe: Subject<void> = new Subject();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor(
    private readonly dataSourceService: DataSourceService,
    private readonly notificationService: NotificationService,
    private readonly tabulatorService: TabulatorService,
    private readonly deviceService: DevicesService,
    private dialogService: DialogService,
    private fusionTransformationService: FusionTransformationService,
    private dialog: MatDialog,
    private cdRef: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {
    this.table = this.tabulatorService.commonTableConfiguration();
    this.mappingTable = this.tabulatorService.commonTableConfiguration();
    this.mappingTable.persistenceID = 'uploads-jmep-mapping';
    this.mappingTable.sort = true;
    this.mappingTable.page = true;
    this.mappingTable.height = 500;
    this.mappingTable.rowHeight = 33;
    this.table.persistenceID = 'uploads-settings';
    this.table.persistence.sort = true;
    this.table.persistence.page = true;
    this.table.initialSort = [{ column: 'version', dir: 'desc' }];
    // Always set table height
    this.table.height = 500;
    this.table.rowHeight = 33;
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method, @typescript-eslint/no-empty-function
  ngOnInit(): void {
    this.getJmepImagesList(false);
    this.getLatestFirmwareVersion();
    this.getJmepKeysFileInfo();
    this.updateFileAccept(this.selectedAgentType);
  }

  ngAfterViewInit() {
    this.dataAvailable = true;
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onActionClick(action: any): any {
    switch (action.label) {
      case 'remove':
        this.showDeleteImageConfirm(action.rowData);
        break;
      case 'download':
        this.downloadAgentImage(action.rowData);
        break;
      default:
        console.warn('Unknown action:', action.label);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  downloadAgentImage(rowData: any): void {
    const fileName = rowData.image;
    const agentType = rowData.type.toLowerCase();
    let filePath = '';

    if (agentType.includes('jmep')) {
      filePath = '/uploads/jmep/images/' + fileName;
      //https://fusion-ctl-10.ral.ds.jdsu.net - If localhost, add this before the url for successfull download
    } else {
      filePath = rowData?.url ? rowData?.url : '';
    }

    const downloadAnchor = document.createElement('a');
    downloadAnchor.href = filePath;
    downloadAnchor.download = filePath.substring(filePath.lastIndexOf('/') + 1);

    // Append, trigger click, and remove anchor element for download
    this.elementRef.nativeElement.appendChild(downloadAnchor);
    downloadAnchor.click();
    this.elementRef.nativeElement.removeChild(downloadAnchor);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onActionFieldMappingClick(action: any): any {
    this.isLoading = true;
    switch (action.label) {
      case 'apply':
        this.applyJmepsBulkRenaming(action.rowData); // Call the specific method for 'Apply'
        break;
      case 'download':
        this.downloadJmepsBulkRenamingFile(action.rowData); // Call the method for 'Download Input File'
        break;
      case 'remove':
        this.showDeleteFieldMappingConfirm(action.rowData); // Call the method for 'Remove'
        break;
      case 'results':
        this.sendViewLastResultsAction(action.rowData); // Call the method for 'View Last Results'
        break;
      default:
        console.warn('Unknown action:', action.label);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  sendViewLastResultsAction(fileName: any): any {
    this.isLoading = false;
    const fileNameData = fileName.fileName;
    this.dialog.open(ViewResultsDialogComponent, {
      width: '1100px',
      height: '630px',
      data: { fileNameData }
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  showDeleteFieldMappingConfirm(data: any): void {
    this.isLoading = false;
    const params = {
      id: 'uploadsFieldMapping.delete',
      alternateTitle: `Remove ${data.id} ?`,
      callBackFnParams: data,
      callBackService: this,
      callBackFuntion: this.callBackFieldMappingRemoveFunction
    };
    this.dialogService.showDialog('confirm', 'body', params);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  showDeleteImageConfirm(imageName: any): void {
    const params = {
      id: 'uploads.delete',
      alternateTitle: `Remove ${imageName.id} ?`,
      callBackFnParams: imageName,
      callBackService: this,
      callBackFuntion: this.callBackRemoveFunction
    };
    this.dialogService.showDialog('confirm', 'body', params);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  callBackRemoveFunction(service: any, params: any): void {
    service.removeVersion(params);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  callBackFieldMappingRemoveFunction(service: any, params: any): void {
    service.showRemoveBulkRenamingFileConfirm(params);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onAgentTypeChange(event: MatSelectChange) {
    this.clearSelectedFile();
    this.selectedAgentType = event.value;
    this.updateFileAccept(this.selectedAgentType);
  }

  updateFileAccept(agentType: string): void {
    // Update the fileAccept value based on the selected agent type
    switch (agentType) {
      case 'JMEP':
      case 'vSPA':
        this.fileAccept = '.pkg';
        break;
      case 'MAP-2100':
        this.fileAccept = '.zip, .exe';
        break;
      case 'QT600-10':
        this.fileAccept = '.tar';
        break;
      default:
        this.fileAccept = '*'; // Allow all file types as fallback
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  changeTab(event: any): void {
    switch (event.index) {
      case 0:
        this.selectedAgentType = null;
        this.cdRef.detectChanges();

        setTimeout(() => {
          this.selectedAgentType =
            this.uploadAgentType.length > 0 ? this.uploadAgentType[0].value : null;
          this.cdRef.detectChanges();
        });
        this.fileAccept = '.tar';
        break;
      case 1:
        this.fileAccept = '.enc';
        break;
      case 2:
        this.fileAccept = '.csv';
        break;
      default:
        this.fileAccept = ''; // Accept all files if no tab is active
    }
    if (event.tab.textLabel === 'JMEP Field Mapping') {
      this.getAllJMEPFieldMappingFiles();
    }
    this.clearSelectedFile();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (file) {
      const fileExtension = '.' + file.name.split('.').pop(); // Get file extension (e.g., .zip, .exe)

      // Validate if the file extension is allowed
      if (this.fileAccept === '*' || this.fileAccept.includes(fileExtension)) {
        this.isValidAgentFileExtension = false; // Valid file
      } else {
        this.isValidAgentFileExtension = true; // Invalid file type
      }
      this.showClearButton = true;
      if (this.selectedAgentType.toLowerCase().includes('jmep')) {
        // if (file.name.endsWith('.pkg')) {
        this.selectedFile = file; // Store the selected file
        this.selectedFileName = file.name; // Update the file name
        // }
      } else if (!this.selectedAgentType.toLowerCase().includes('jmep')) {
        this.selectedFile = file; // Store the selected file
        this.selectedFileName = file.name; // Update the file name
      } else {
        this.clearSelectedFile(); // Clear the input if invalid
      }
    }
  }

  getLatestFirmwareVersion(): void {
    this.deviceService
      .getGenericAgentConfigDetail()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          if (response) {
            this.agentImageList = [];
            const agentLinks = response.latestFirmwareImages;
            if (agentLinks) {
              this.uploadAgentType.forEach(typeInfo => {
                const imageInfoList = agentLinks[typeInfo.value];
                if (imageInfoList) {
                  imageInfoList.forEach(imageInfo => {
                    const imageNameItems = imageInfo.image.split('/');
                    let imageName = imageNameItems[imageNameItems.length - 1];
                    if (typeInfo.value === AGENT_MODEL.VSPA) {
                      // For vMA, take the last part of the name
                      imageName = imageNameItems[imageNameItems.length - 1];
                    } else if (
                      typeInfo.value === AGENT_MODEL.QT600 ||
                      typeInfo.value === AGENT_MODEL.MAP2100
                    ) {
                      // For QT600 and MAP2100, take the last part and remove "AgentImage_" prefix
                      imageName = imageNameItems[imageNameItems.length - 1].replace(
                        'AgentImage_',
                        ''
                      );
                    }
                    const agentData = {
                      id: imageName,
                      image: imageName,
                      url: imageInfo.image,
                      version: imageInfo.version,
                      type: typeInfo.value
                    };
                    this.agentImageList.push(agentData);
                  });
                }
              });
              this.transformJMEPImageData();
            }
          }
        },
        error => {}
      );
    this.isLoading = false;
  }

  browseFiles(): void {
    if (this.file) {
      this.file.nativeElement.click();
      this.showClearButton = true;
    }
  }

  uploadImages(): any {
    if (!this.selectedFile || !this.selectedAgentType) {
      this.notificationService.onFail('Please select both a file and an agent type.');
      this.clearSelectedFile();
      return;
    }
    this.isLoading = true;

    if (this.selectedAgentType.toLowerCase().includes('jmep')) {
      const fileArr = [];
      fileArr.push(this.selectedFile);
      this.uploadFile(fileArr, 'jmepImage', 'jmepImage');
    } else {
      const formData = new FormData();
      formData.append('file', this.selectedFile);
      formData.append('agentType', this.selectedAgentType);
      this.dataSourceService
        .uploadAgentFile(formData)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          response => {
            const data = response;
            this.isLoading = false;
            this.showClearButton = true;
            this.clearSelectedFile();
            this.getLatestFirmwareVersion();
            this.dialogService.showInfo(
              'information',
              'body',
              `Agent image uploaded successfully.</br></br> <b>MD5 Checksum:</b> ${response.checksum}`,
              'Agent Image',
              response.checksum
            );
          },
          error => {
            this.isLoading = false;
            this.notificationService.onFail('Agent image upload failed. Please try again');
          }
        );
    }
  }

  uploadJmepActivationKeys(): any {
    if (!this.selectedFile) {
      this.notificationService.onFail('Please select a file.');
      this.clearSelectedFile();
      return;
    }
    this.isLoading = true;
    const fileArr = [];
    fileArr.push(this.selectedFile);
    this.uploadFile(fileArr, 'jmepSecurityKeys', 'jmepSecurityKeys');
  }

  uploadJmepFieldMapping(): any {
    if (!this.selectedFile) {
      this.notificationService.onFail('Please select a file.');
      this.clearSelectedFile();
      return;
    }
    this.isLoading = true;
    const formData = new FormData();
    formData.append('configFile', this.selectedFile);
    formData.append('groupName', 'rename');
    this.dataSourceService
      .uploadJmepsBulkConfigFile(formData)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.isLoading = false;
          this.uploadProcessing = false;
          this.showProgressBar = false;
          this.clearSelectedFile();
          if (response.status === 'success') {
            this.notificationService.onSuccess('Jmep Field Mapping File uploaded successfully');
            this.getAllJMEPFieldMappingFiles();
          } else {
            this.notificationService.onFail(
              'JMEP Field mapping File upload failed. Please try again.'
            );
          }
        },
        err => {
          this.isLoading = false;
          this.uploadProcessing = false;
          this.showProgressBar = false;
          this.notificationService.onFail(
            'JMEP Field mapping File upload failed. Please try again.'
          );
        }
      );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  uploadFile(fileArr, fileLabel: string, fileType: string, fileSize?: number): void {
    this.uploadMsg = '';
    this.uploadErrorMsg = '';

    if (fileArr.length > 0) {
      this.uploadProcessing = true;
      this.showProgressBar = true;
      this.progressCompleted = 0;
      this.uploadMsg = `Uploading ${fileLabel}...`;

      const myFile = fileArr[0];
      if (fileSize && myFile.size > fileSize) {
        this.uploadMsg = '';
        this.uploadErrorIcon = 'warning';
        this.uploadErrorMsg = `${fileLabel} cannot be larger than ${this.transformFilesize(
          fileSize
        )}`;
      } else {
        this.uploadFileList(myFile, fileType);
      }
    } else {
      this.notificationService.onFail('Please upload a file');
    }
  }

  transformFilesize(fileSize: number): string {
    if (fileSize && !isNaN(fileSize)) {
      if (fileSize / 1000000000 > 1) {
        return `${Math.floor(fileSize / 1000000000)}GB`;
      }
      if (fileSize / 1000000 > 1) {
        return `${Math.floor(fileSize / 1000000)}MB`;
      }
      if (fileSize / 1000 > 1) {
        return `${Math.floor(fileSize / 1000)}KB`;
      }
      return `${fileSize}B`;
    }
    return '';
  }

  uploadFileList(file: File, fileType: string): any {
    const formData: FormData = new FormData();
    if (fileType === 'jmepImage') {
      formData.append('jmepImage', file);
    } else if (fileType === 'jmepSecurityKeys') {
      formData.append('jmepSecurityKeys', file);
    } else if (fileType === 'jmepFieldMapping') {
      formData.append('jmepFieldMapping', file);
    }

    this.dataSourceService
      .jmepUpload(formData)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          if (response.status === 'success') {
            this.isLoading = false;
            if (fileType === 'jmepImage') {
              this.getJmepImagesList(true);
              this.dialogService.showInfo(
                'information',
                'body',
                `Agent image uploaded successfully.</br></br> <b>MD5 Checksum:</b> ${response.data.checksum}`,
                'Agent Image',
                response.data.checksum
              );
            } else if (fileType === 'jmepSecurityKeys') {
              this.getJmepKeysFileInfo();
              this.notificationService.onSuccess('Jmep Activation File uploaded successfully');
            } else if (fileType === 'jmepFieldMapping') {
              this.notificationService.onSuccess('Jmep Field Mapping File uploaded successfully');
              this.getAllJMEPFieldMappingFiles();
            }
            this.clearSelectedFile();
          } else {
            this.isLoading = false;
            this.notificationService.onFail('File upload failed. Please try again.');
            this.uploadProcessing = false;
            this.showProgressBar = false;
          }
        },
        error => {
          this.isLoading = false;
          this.notificationService.onFail('File upload failed. Please try again.');
          this.uploadProcessing = false;
          this.showProgressBar = false;
        }
      );
  }

  getJmepKeysFileInfo(): void {
    this.dataSourceService
      .getJmepKeysInfo()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        if (response.stats) {
          const modTime = new Date(response.stats.mtime);
          this.jmepKeysFileDateString = modTime.toLocaleString();
        }
        if (response.fileName) {
          this.jmepKeysFileName = response.fileName;
        }
      });
  }

  redistributeJMEPKeys(): void {
    this.isLoading = true;
    this.dataSourceService
      .getRedistributeJMEPKeys()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.isLoading = false;
          if (response && response.status === 'complete' && response.data.length > 0) {
            // Open dialog and pass the response data to it
            this.showJmepKeyAppliedStatus(response.data);
          }
        },
        error => {
          this.isLoading = false;
        }
      );
  }

  // eslint-disable-next-line
  showJmepKeyAppliedStatus(status: any): void {
    const dialogRef = this.dialog.open(DataDisplayDialogComponent, {
      width: '700px',
      height: '420px',
      data: status,
      panelClass: 'custom-dialog-container'
    });
  }

  getJmepImagesList(isJmepImage?: boolean): void {
    this.dataSourceService
      .getjmepImagesList()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        if (response.status === 'success') {
          this.jmepImageList = response.files.map(item => this.getJMEPImageTableObject(item));
          if (isJmepImage) {
            this.transformJMEPImageData();
          }
        }
      });
  }

  transformJMEPImageData(): void {
    const combineList = [...this.jmepImageList, ...this.agentImageList];
    const imagesData = this.fusionTransformationService.transformData(
      'uploads-settings',
      combineList
    );
    this.table.columns = imagesData.column;
    this.table.data = imagesData.data;
    this.table = Object.assign({}, this.table);
  }

  // Create a table object from a JMEP image
  getJMEPImageTableObject(item: string): any {
    return {
      id: item,
      image: item,
      version: item.split('_')[1].split('.pkg')[0],
      type: 'JMEP'
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getJMEPFieldMappingTableObject(item: any): any {
    return {
      id: item.inputFilename,
      fileName: item.inputFilename,
      uploadTime: this.formatTimestamp(item.inputFileTime),
      resultsTime: item.previousJobResultSummary
        ? this.formatTimestamp(item.previousJobResultSummary.endTime)
        : null,
      status:
        item.activeJobStatus && item.activeJobStatus.jobStatus === 'APPLY_IN_PROGRESS'
          ? 'inProgress'
          : '--'
    };
  }

  formatTimestamp(timestamp: string | number): string {
    return new Date(Number(timestamp)).toLocaleString('en-US', { hour12: false });
  }

  getAllJMEPFieldMappingFiles(): any {
    this.dataSourceService
      .getAllBulkRenamingFilesList()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        if (response.status === 'success') {
          if (response.data && response.data.length > 0) {
            this.showBulkRenamingFiles = true;
            this.getAllMetaDataFileResponse = response.data;
          } else {
            this.showBulkRenamingFiles = false;
          }
          this.jmepFieldMappingFilesList = this.transformFieldMappingListData(response.data);
          const imagesData = this.fusionTransformationService.transformData(
            'uploads-jmep-mapping',
            this.jmepFieldMappingFilesList
          );
          this.mappingTable.columns = imagesData.column;
          this.mappingTable.data = imagesData.data;
          this.mappingTable = Object.assign({}, this.mappingTable);
        }
        if (!this.getJmepFileListReq) {
          this.getJmepFileListReq = setTimeout(this.getAllJMEPFieldMappingFiles(), 10000);
        }
      });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  transformFieldMappingListData(data: any): any {
    const temp = [];
    if (data.length > 0) {
      data.forEach(item => {
        temp.push(this.getJMEPFieldMappingTableObject(item));
      });
      return temp;
    }
  }

  clearSelectedFile(): void {
    this.files.clear();
    if (this.file && this.file.nativeElement) {
      this.file.nativeElement.value = '';
    }
    this.selectedAgentType = this.uploadAgentType[0].value;
    this.selectedFileName = '';
    this.showClearButton = false;
    this.isValidAgentFileExtension = false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  removeVersion(data: any): void {
    this.isLoading = true;
    const type = data?.rowData?.type || data?.type;
    const version = data?.rowData?.version || data?.version;
    const image = data?.rowData?.image || data?.image;
    if (!type?.toLowerCase().includes('jmep')) {
      this.dataSourceService
        .nonJmepDelete(type, version)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(response => {
          this.isLoading = false;
          this.notificationService.onSuccess('Agent image removed Successfully');
          this.getLatestFirmwareVersion();
        });
    } else {
      this.dataSourceService.jmepDelete(image).subscribe(response => {
        if (response) {
          this.isLoading = false;
          this.notificationService.onSuccess('Agent image removed Successfully');
          this.getJmepImagesList(true);
        }
      });
    }
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  applyJmepsBulkRenaming(fileName: any): void {
    this.isLoading = false;
    this.applyRenamingDialogRef = this.dialog.open(BulkRenamingDialogComponent, {
      width: '600px',
      height: '400px',
      disableClose: true,
      data: { fileName: fileName.fileName }
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  downloadJmepsBulkRenamingFile(fileName: any): void {
    const downloadFileName = fileName.fileName;
    this.isLoading = false;
    const downloadAnchor = document.createElement('a');
    const filePath = `${this.jmepBulkConfigInputFileDownloadPath}&fileName=${downloadFileName}`;
    downloadAnchor.href = filePath;
    downloadAnchor.download = filePath.substring(filePath.lastIndexOf('/') + 1);

    // Append, trigger click, and remove anchor element for download
    this.elementRef.nativeElement.appendChild(downloadAnchor);
    downloadAnchor.click();
    this.elementRef.nativeElement.removeChild(downloadAnchor);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  showRemoveBulkRenamingFileConfirm(data: any): any {
    this.isLoading = true;
    this.dataSourceService
      .removeBulkRenamingFile(data.fileName)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        this.isLoading = false;
        this.notificationService.onSuccess('File removed Successfully');
        this.getAllJMEPFieldMappingFiles();
      });
  }
}
